import { useFetch } from '@/hooks/api'
import { useAuth } from '@/hooks/useAuth'
import {
  formatDate,
  neutralizeDate,
  normalizeCnpj,
  normalizeCurrency
} from '@/utils/normalize'
import { format, subDays } from 'date-fns'
import ptBR from 'date-fns/locale/pt-BR'
import { useRouter } from 'next/router'
import { BsFileEarmarkCheckFill } from 'react-icons/bs'
import { GiReceiveMoney } from 'react-icons/gi'
import { IoDocumentAttachSharp } from 'react-icons/io5'
import {
  MdAddCircleOutline,
  MdInbox,
  MdOutlineWidgets,
  MdQueryBuilder,
  MdReceiptLong,
  MdWatchLater
} from 'react-icons/md'
import Lottie from 'react-lottie'
import * as animationData from '@/assets/90507-money-saving.json'
import { HasPermission } from '@/components/ui/HasPermission'
import { ModalConfigDashboard } from '@/components/ui/ModalConfigDashboard'
import { useEffect, useMemo, useState } from 'react'
import { bfcDigital } from '@/services/bfcDigital'
import { getConfirmationStyle } from '@/components/pages/EConfirmation/utils'
import { ConfirmationGraphic } from '@/components/ui/ConfirmationGraph'
import { OperationGraphic } from '@/components/ui/OperationGraph'
import { IoMdAnalytics } from 'react-icons/io'
import { PermissionsList } from '@/@types/permission_list'
import { Container } from '@/components/pages/Home/styles'
import {
  Accent,
  DashCard,
  DashKpi,
  DashLegend,
  DashListState,
  DashRow
} from '@/components/pages/Home/parts'

interface ChartConfirmation {
  id: TypeEConfirmation
  label: string
  color: string
  data: {
    x: string
    y: number
  }[]
}

/** Altura dos widgets. Fixa e igual para todos os cartões de uma linha, para
 *  que a grade não fique serrilhada quando o usuário liga/desliga widgets.
 *  Em px porque a raiz do projeto não é 16px (globals.ts: html { 88% }). */
const CARD_HEIGHT = 362
const CARD_HEIGHT_WIDE = 440

const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1)

/** Os status chegam da API sem acento e em caixa baixa; a legenda e o balão
 *  do gráfico mostram a forma escrita por extenso. */
const CONFIRMATION_LABEL: Partial<Record<TypeEConfirmation, string>> = {
  'a confirmar': 'A confirmar',
  confirmado: 'Confirmado',
  inexistente: 'Inexistente',
  'nao confirmado': 'Não confirmado',
  'nfe nao encontrada': 'NFe não encontrada',
  rejeitado: 'Rejeitado',
  expirado: 'Expirado'
}

/** Uma série por status, na ordem em que os índices são preenchidos abaixo. */
const buildConfirmationSeries = (
  ids: TypeEConfirmation[]
): ChartConfirmation[] =>
  ids.map(id => ({
    id,
    label: CONFIRMATION_LABEL[id] || capitalize(id),
    color: getConfirmationStyle(id).backgroundColor,
    data: []
  }))

/** Séries sem nenhum valor no mês não entram na legenda. */
const activeSeries = (chart: ChartConfirmation[]) =>
  chart
    .map(serie => ({
      label: serie.label,
      color: serie.color,
      total: serie.data.reduce((sum, point) => sum + (point.y || 0), 0)
    }))
    .filter(serie => serie.total > 0)

export default function Home(): JSX.Element {
  const { user } = useAuth()
  const router = useRouter()

  const [nfeConfirmation, setNfeConfirmation] = useState<
    {
      aConfirmar: number
      confirmadas: number
      dia: number
      naoConfirmadas: number
      naoEncontradas: number
      rejeitadas: number
    }[]
  >([])
  const nfeChart = useMemo(() => {
    const chart = buildConfirmationSeries([
      'a confirmar',
      'confirmado',
      'inexistente',
      'nao confirmado',
      'nfe nao encontrada',
      'rejeitado',
      'expirado'
    ])
    nfeConfirmation.forEach(item => {
      chart[0].data.push({
        x: String(item.dia),
        y: item.aConfirmar
      })
      chart[1].data.push({
        x: String(item.dia),
        y: item.confirmadas
      })
      chart[3].data.push({
        x: String(item.dia),
        y: item.naoConfirmadas
      })
      chart[4].data.push({
        x: String(item.dia),
        y: item.naoEncontradas
      })
      chart[5].data.push({
        x: String(item.dia),
        y: item.rejeitadas
      })
    })
    return chart
  }, [nfeConfirmation])

  const [cteConfirmation, setCteConfirmation] = useState<
    {
      aConfirmar: number
      confirmadas: number
      dia: number
      naoConfirmadas: number
      naoEncontradas: number
      rejeitadas: number
    }[]
  >([])
  const cteChart = useMemo(() => {
    const chart = buildConfirmationSeries([
      'a confirmar',
      'confirmado',
      'inexistente',
      'nao confirmado',
      'rejeitado',
      'expirado'
    ])
    cteConfirmation.forEach(item => {
      chart[0].data.push({
        x: String(item.dia),
        y: item.aConfirmar
      })
      chart[1].data.push({
        x: String(item.dia),
        y: item.confirmadas
      })
      chart[3].data.push({
        x: String(item.dia),
        y: item.naoConfirmadas
      })
      chart[4].data.push({
        x: String(item.dia),
        y: item.rejeitadas
      })
    })
    return chart
  }, [cteConfirmation])

  useEffect(() => {
    bfcDigital
      .get('/requestlist/dashboard/month?system=erp')
      .then(res =>
        setNfeConfirmation(
          res.data.filter(d => d.dia <= neutralizeDate(new Date()).getDate())
        )
      )
      .catch(() => {
        //
      })
  }, [])

  useEffect(() => {
    bfcDigital
      .get('/bordero-report-cte/dashboard/month?system=erp')
      .then(res =>
        setCteConfirmation(
          res.data.filter(d => d.dia <= neutralizeDate(new Date()).getDate())
        )
      )
      .catch(() => {
        //
      })
  }, [])

  const { data: lastAssignors } = useFetch(
    '/assignors?page=1&per_page=15&order_name=createDate&order_direction=desc'
  )
  const { data: operationsPaid } = useFetch(
    '/operations?page=1&per_page=15&status_id=11&order_name=date&order_direction=desc'
  )
  const { data: operationsOpen } = useFetch(
    '/operations?page=1&per_page=15&status_id=2,3,4,7,8,9,10,6&order_name=updatedAt&order_direction=desc'
  )
  const { data: titlesPaid } = useFetch(
    '/titles?page=1&per_page=15&situation=paid&order_name=paidDate&order_direction=desc'
  )
  const { data: assignorsReport } = useFetch('/dashboard/assignors')
  const { data: operationsReport } = useFetch('/dashboard/operations')

  const today = neutralizeDate(new Date())
  const periodLabel = `${format(subDays(today, 29), "dd 'de' MMM", {
    locale: ptBR
  })} a ${format(today, "dd 'de' MMM", { locale: ptBR })}`
  const monthLabel = capitalize(format(today, 'MMMM', { locale: ptBR }))

  const nfeLegend = useMemo(() => activeSeries(nfeChart), [nfeChart])
  const cteLegend = useMemo(() => activeSeries(cteChart), [cteChart])

  const operationsChart = useMemo(
    () =>
      operationsReport?.list
        ?.map(d => ({
          x: `${d.date.split('-')[2]}/${d.date.split('-')[1]}`,
          total: d.total,
          date: `${d.date.split('-')[2]}/${d.date.split('-')[1]}`,
          paid_value: d.paid_value
        }))
        .slice(0)
        .reverse() || [],
    [operationsReport]
  )

  const widgets: {
    id: string
    name: string
    permission: string
    cols: number
    height: number
    accent: Accent
    element: JSX.Element
  }[] = [
    {
      id: 'operations-day-report',
      name: 'Operações pagas por dia',
      permission: 'Operações pagas por dia',
      cols: 12,
      height: CARD_HEIGHT_WIDE,
      accent: 'blue',
      element: (
        <DashCard
          icon={<IoMdAnalytics />}
          title="Operações pagas por dia"
          subtitle="Antecipação de Recebíveis, Cartão de Crédito, CCB e Precatórios"
          pill="Últimos 30 dias"
        >
          <div className="dash-kpis is-contained">
            <DashKpi
              label="Operações"
              value={operationsReport?.total.total ?? '—'}
            />
            <DashKpi
              label="Valor pago"
              accent
              value={
                operationsReport
                  ? normalizeCurrency(operationsReport.total.paid_value, true)
                  : '—'
              }
            />
            <DashKpi
              label="Média por dia"
              value={operationsReport?.media.total ?? '—'}
            />
            <DashKpi
              label="Média do valor"
              value={
                operationsReport
                  ? normalizeCurrency(operationsReport.media.paid_value, true)
                  : '—'
              }
            />
          </div>

          <div className="dash-split">
            <div>
              <span className="dash-chart__label">Por quantidade</span>
              <div className="dash-chart">
                <OperationGraphic
                  data={[
                    {
                      id: 'operations',
                      label: 'Operações',
                      color: '#1b56a3',
                      data: operationsChart.map(d => ({ ...d, y: d.total }))
                    }
                  ]}
                />
              </div>
            </div>
            <div>
              <span className="dash-chart__label">Por valor pago</span>
              <div className="dash-chart">
                <OperationGraphic
                  currency
                  data={[
                    {
                      id: 'operations',
                      label: 'Operações',
                      color: '#0f7b3f',
                      data: operationsChart.map(d => ({
                        ...d,
                        y: d.paid_value
                      }))
                    }
                  ]}
                />
              </div>
            </div>
          </div>
        </DashCard>
      )
    },
    {
      id: 'assignors-report',
      name: 'Estatística de Cadastro',
      permission: 'Estatística de Cadastro',
      cols: 8,
      height: CARD_HEIGHT,
      accent: 'cyan',
      element: (
        <DashCard
          icon={<IoMdAnalytics />}
          accent="cyan"
          title="Estatística de Cadastro"
          subtitle={periodLabel}
          pill="30 dias"
        >
          <div className="dash-kpis">
            <DashKpi label="Total" value={assignorsReport?.total ?? '—'} />
            <DashKpi
              label="Aprovados"
              accent
              value={assignorsReport?.approved ?? '—'}
            />
            <DashKpi
              label="Em análise"
              value={assignorsReport?.finished ?? '—'}
            />
            <DashKpi
              label="Incompletos"
              value={assignorsReport?.incompletes ?? '—'}
            />
          </div>

          <div className="dash-progress">
            <span
              style={{
                width: `${
                  assignorsReport
                    ? (assignorsReport.approved * 100) / assignorsReport.total
                    : 0
                }%`,
                background: '#12b76a'
              }}
            />
            <span
              style={{
                width: `${
                  assignorsReport
                    ? (assignorsReport.finished * 100) / assignorsReport.total
                    : 0
                }%`,
                background: '#2e90fa'
              }}
            />
            <span
              style={{
                width: `${
                  assignorsReport
                    ? (assignorsReport.incompletes * 100) /
                      assignorsReport.total
                    : 0
                }%`,
                background: '#d0d5dd'
              }}
            />
          </div>
          <DashLegend
            items={[
              {
                label: 'Aprovados',
                color: '#12b76a',
                value: assignorsReport?.approved
              },
              {
                label: 'Em análise',
                color: '#2e90fa',
                value: assignorsReport?.finished
              },
              {
                label: 'Incompletos',
                color: '#d0d5dd',
                value: assignorsReport?.incompletes
              }
            ]}
          />

          <div className="dash-chart mt-2">
            <ConfirmationGraphic
              area
              unit="cadastro(s)"
              data={[
                {
                  id: 'cadastro',
                  label: 'Cadastro',
                  color: '#1b56a3',
                  data:
                    assignorsReport?.last30DaysRegisters?.days
                      ?.map(d => ({
                        x: `${d.date.split('/')[0]}/${d.date.split('/')[1]}`,
                        y: d.total
                      }))
                      .slice(0)
                      .reverse() || []
                }
              ]}
            />
          </div>
        </DashCard>
      )
    },
    {
      id: 'last-assignors',
      name: 'Novos Cadastros',
      permission: 'Novos Cadastros',
      cols: 4,
      height: CARD_HEIGHT,
      accent: 'cyan',
      element: (
        <DashCard
          flush
          icon={<IoDocumentAttachSharp />}
          accent="cyan"
          title="Novos Cadastros"
          subtitle="Cedentes criados recentemente"
        >
          <div className="dash-card__scroll">
            <DashListState
              loading={!lastAssignors}
              isEmpty={!lastAssignors?.data?.length}
              icon={<IoDocumentAttachSharp />}
              emptyTitle="Nenhum cadastro novo"
              emptyText="Cedentes recém-criados aparecem aqui."
            >
              {lastAssignors?.data?.map((assignor: Assignor) => (
                <DashRow
                  key={assignor.id}
                  label={assignor.name}
                  onClick={() =>
                    router.push(`/assignors/create?id=${assignor.id}`)
                  }
                  title={assignor.name}
                  sub={[assignor?.code, normalizeCnpj(assignor.cnpj)]
                    .filter(Boolean)
                    .join(' · ')}
                  tag={
                    assignor.is_approved
                      ? { label: 'Aprovado', tone: 'green' }
                      : assignor.is_finished
                      ? { label: 'Em análise', tone: 'blue' }
                      : { label: 'Incompleto', tone: 'gray' }
                  }
                />
              ))}
            </DashListState>
          </div>
        </DashCard>
      )
    },
    {
      id: 'operations-paid',
      name: 'Operações Pagas',
      permission: 'Operações Pagas',
      cols: 3,
      height: CARD_HEIGHT,
      accent: 'green',
      element: (
        <DashCard
          flush
          icon={<GiReceiveMoney />}
          accent="green"
          title="Operações Pagas"
          subtitle="Últimas liquidações"
        >
          <div className="dash-card__scroll">
            <DashListState
              loading={!operationsPaid}
              isEmpty={!operationsPaid?.data?.length}
              icon={<GiReceiveMoney />}
              emptyTitle="Nenhuma operação paga"
              emptyText="As liquidações mais recentes aparecem aqui."
            >
              {operationsPaid?.data?.map((operation: Operation) => (
                <DashRow
                  key={operation.id}
                  label={operation?.assignor?.name}
                  onClick={() =>
                    router.push(`/operations/register?id=${operation.id}`)
                  }
                  title={
                    operation?.assignor?.name || `Operação ${operation.code}`
                  }
                  sub={`Nº ${operation.code}`}
                  value={normalizeCurrency(operation.net_value, true)}
                  valueIsPositive
                  date={formatDate(operation.date)}
                />
              ))}
            </DashListState>
          </div>
        </DashCard>
      )
    },
    {
      id: 'operations-open',
      name: 'Operações Em Aberto',
      permission: 'Operações Em Aberto',
      cols: 5,
      height: CARD_HEIGHT,
      accent: 'amber',
      element: (
        <DashCard
          flush
          icon={<MdWatchLater />}
          accent="amber"
          title="Operações em aberto"
          subtitle="Aguardando conclusão"
        >
          <div className="dash-card__scroll">
            <DashListState
              loading={!operationsOpen}
              isEmpty={!operationsOpen?.data?.length}
              icon={<MdWatchLater />}
              emptyTitle="Nada em aberto"
              emptyText="Todas as operações foram concluídas."
            >
              {operationsOpen?.data?.map((operation: Operation) => (
                <DashRow
                  key={operation.id}
                  label={operation?.assignor?.name}
                  onClick={() =>
                    router.push(`/operations/register?id=${operation.id}`)
                  }
                  title={
                    operation?.assignor?.name || `Operação ${operation.code}`
                  }
                  sub={`Nº ${operation.code} · ${
                    operation?.status?.name || '—'
                  }`}
                  value={normalizeCurrency(operation?.net_value || '000', true)}
                  date={formatDate(operation?.updated_at, true)}
                />
              ))}
            </DashListState>
          </div>
        </DashCard>
      )
    },
    {
      id: 'titles-paid',
      name: 'Títulos Baixados',
      permission: 'Títulos Baixados',
      cols: 4,
      height: CARD_HEIGHT,
      accent: 'slate',
      element: (
        <DashCard
          flush
          icon={<BsFileEarmarkCheckFill />}
          accent="slate"
          title="Títulos Baixados"
          subtitle="Últimas baixas registradas"
        >
          <div className="dash-card__scroll">
            <DashListState
              loading={!titlesPaid}
              isEmpty={!titlesPaid?.data?.length}
              icon={<MdReceiptLong />}
              emptyTitle="Nenhum título baixado"
              emptyText="As baixas mais recentes aparecem aqui."
            >
              {titlesPaid?.data?.map((title: Title) => (
                <DashRow
                  key={title.id}
                  label={title.payer?.name}
                  onClick={() => router.push(`/titles?id=${title.id}`)}
                  title={title.payer?.name || title.document_number}
                  sub={`Doc. ${title.document_number}`}
                  value={normalizeCurrency(title?.value, true)}
                  date={formatDate(title?.paid_date)}
                />
              ))}
            </DashListState>
          </div>
        </DashCard>
      )
    },
    {
      id: 'nfe-confirmation',
      name: 'NFe-s Analisadas',
      permission: 'NFe-s Analisadas',
      cols: 6,
      height: CARD_HEIGHT,
      accent: 'blue',
      element: (
        <DashCard
          icon={<IoMdAnalytics />}
          title="NFe-s Analisadas"
          subtitle="Situação das notas por dia do mês"
          pill={monthLabel}
        >
          <div className="dash-chart">
            <ConfirmationGraphic data={nfeChart} unit="NFe-s" maxTicks={10} />
          </div>
          {nfeLegend.length ? (
            <DashLegend
              items={nfeLegend.map(serie => ({
                label: serie.label,
                color: serie.color,
                value: serie.total
              }))}
            />
          ) : null}
        </DashCard>
      )
    },
    {
      id: 'cte-confirmation',
      name: 'CTe-s Analisadas',
      permission: 'CTe-s Analisadas',
      cols: 6,
      height: CARD_HEIGHT,
      accent: 'cyan',
      element: (
        <DashCard
          icon={<IoMdAnalytics />}
          accent="cyan"
          title="CTe-s Analisadas"
          subtitle="Situação dos conhecimentos por dia do mês"
          pill={monthLabel}
        >
          <div className="dash-chart">
            <ConfirmationGraphic data={cteChart} unit="CTe-s" maxTicks={10} />
          </div>
          {cteLegend.length ? (
            <DashLegend
              items={cteLegend.map(serie => ({
                label: serie.label,
                color: serie.color,
                value: serie.total
              }))}
            />
          ) : null}
        </DashCard>
      )
    }
  ]

  const [widgetVisible, setWidgetVisible] = useState<string[]>(() => {
    const d = localStorage.getItem('widgets__dash')
    if (d) {
      return JSON.parse(d)
    }
    return []
  })

  useEffect(() => {
    localStorage.setItem('widgets__dash', JSON.stringify(widgetVisible))
  }, [widgetVisible])

  const [config, setConfig] = useState(false)

  /* A ordem é a da lista de widgets, não a da ordem em que o usuário foi
     ligando as caixinhas — senão a grade sai remontada a cada troca. */
  const visibleWidgets = widgets.filter(w => widgetVisible.includes(w.id))

  const shortcuts: {
    permission: PermissionsList
    label: string
    to: string
  }[] = [
    {
      permission: 'Adicionar Operações',
      label: 'Nova Operação',
      to: '/operations/register'
    },
    {
      permission: 'Adicionar Cedente',
      label: 'Novo Cedente',
      to: '/assignors/create'
    },
    {
      permission: 'Adicionar Sacados',
      label: 'Novo Sacado',
      to: '/payers/create'
    }
  ]

  return (
    <Container>
      {config && (
        <ModalConfigDashboard
          onHide={() => {
            setConfig(false)
          }}
          onUpdateItem={item => {
            if (widgetVisible.find(w => w === item)) {
              setWidgetVisible(widgetVisible.filter(w => w !== item))
            } else {
              setWidgetVisible([...widgetVisible, item])
            }
          }}
          widgetVisible={widgetVisible}
          widgets={widgets.map(w => ({
            id: w.id,
            name: w.name,
            permission: w.permission as any
          }))}
        />
      )}

      <header className="dash-head">
        <div className="dash-head__id">
          <div className="dash-head__art">
            <Lottie
              options={{
                animationData,
                loop: true,
                autoplay: true
              }}
              height={74}
              width={74}
            />
          </div>
          <div>
            <strong className="dash-head__greeting">
              Olá, {user?.name?.split(' ')[0]}
            </strong>
            <span className="dash-head__meta">
              <span>Bem-vindo à BFC Digital</span>
              <i className="dash-head__dot" />
              <span>
                <MdQueryBuilder />
                Atualizado às {format(today, "HH:mm 'de' dd/MM/yyyy")}
              </span>
            </span>
          </div>
        </div>

        <div className="dash-head__actions">
          {shortcuts.map(shortcut => (
            <HasPermission permission={shortcut.permission} key={shortcut.to}>
              <button
                type="button"
                className="dash-action"
                onClick={() => router.push(shortcut.to)}
              >
                <MdAddCircleOutline />
                {shortcut.label}
              </button>
            </HasPermission>
          ))}
          <span className="dash-head__sep" />
          <button
            type="button"
            className="dash-action is-neutral"
            onClick={() => setConfig(true)}
          >
            <MdOutlineWidgets />
            Widgets
          </button>
        </div>
      </header>

      {visibleWidgets.length === 0 ? (
        <div className="dash-blank">
          <MdInbox />
          <h5>Nenhum widget em exibição</h5>
          <p>Escolha os indicadores que você quer acompanhar por aqui.</p>
          <button
            type="button"
            className="dash-action"
            onClick={() => setConfig(true)}
          >
            <MdOutlineWidgets />
            Configurar widgets
          </button>
        </div>
      ) : (
        <div className="dash-grid">
          {visibleWidgets.map(item => (
            <HasPermission
              permission={item.permission as PermissionsList}
              key={item.id}
            >
              <div
                className={`dash-cell col-12 col-lg-${item.cols}`}
                style={{ height: item.height, flexGrow: item.cols }}
              >
                {item.element}
              </div>
            </HasPermission>
          ))}
        </div>
      )}
    </Container>
  )
}
